import type { AbstractControl, AsyncValidator, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { NG_ASYNC_VALIDATORS } from '@angular/forms';
import type { Observable } from 'rxjs';
import { of, timer } from 'rxjs';
import { Directive, Input, inject } from '@angular/core';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ESavePageErrorCodes } from '@common/enums/error-cores.enum';
import { ValidatorHttpService } from '@common/services/REST/validator.http.service';

type PageUrlValidatorOptions = {
    siteId: string;
    checkIfDirty?: boolean;
};

export function CheckPageUrlValidator(
    validatorHttpService: ValidatorHttpService,
    options: PageUrlValidatorOptions
): AsyncValidatorFn {
    let initialValue = null;
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> =>
        timer(500).pipe(
            filter(() => {
                if (!options.siteId) {
                    console.warn('PageUrlValidatorDirective: siteId is not defined');
                    return false;
                }

                return true;
            }),
            switchMap(() => {
                if (!control.value) {
                    return of(null);
                }

                if (!initialValue) {
                    initialValue = control.value;
                }

                if (options.checkIfDirty && control.pristine && initialValue === control.value) {
                    return of(null);
                }

                return validatorHttpService.checkPageUrl(options.siteId, control.value);
            }),
            map(() => null),
            catchError((error) => {
                const errorCode = error?.error?.errors?.url[0];

                const errorCodesMap = {
                    [ESavePageErrorCodes.PAGE_URL_RESERVED]: { [ESavePageErrorCodes.PAGE_URL_RESERVED]: true },
                    [ESavePageErrorCodes.PAGE_URL_INVALID]: { [ESavePageErrorCodes.PAGE_URL_INVALID]: true },
                    [ESavePageErrorCodes.PAGE_URL_ALREADY_USED]: { [ESavePageErrorCodes.PAGE_URL_ALREADY_USED]: true },
                };

                if (!errorCode || !errorCodesMap[errorCode]) {
                    return null;
                }

                return of(errorCodesMap[errorCode]);
            })
        );
}

@Directive({
    selector: '[spPageUrlCheck][formControl]',
    providers: [{ provide: NG_ASYNC_VALIDATORS, useExisting: PageUrlValidatorDirective, multi: true }],
})
export class PageUrlValidatorDirective implements AsyncValidator {
    private readonly validatorHttpService = inject(ValidatorHttpService);

    @Input() public spPageUrlOptions: PageUrlValidatorOptions = {
        siteId: null,
        checkIfDirty: false,
    };

    public validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
        return CheckPageUrlValidator(this.validatorHttpService, this.spPageUrlOptions)(control);
    }
}
